import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BetConfig } from '../api/betConfigsApi';
import { GeneralBet, placeBatchGeneralBets } from '../api/generalBetsApi';
import { Group } from '../api/groupsApi';
import { Team } from '../api/teamsApi';
import { GripVertical, Check, X } from 'lucide-react';
import { Badge } from './ui/badge';

interface GroupRankingBetProps {
  qualConfig: BetConfig;
  standingsConfig: BetConfig;
  groups: Group[];
  teams: Team[];
  userBets: Record<string, GeneralBet>;
  bettable: boolean;
  onBetsUpdated: (standingsBets: GeneralBet[], qualBets: GeneralBet[]) => void;
}

function RankCircle({ rank, success }: { rank: number; success?: boolean | null }) {
  const borderClass =
    success === true ? 'border-green-500' :
    success === false ? 'border-red-500' :
    'border-gray-600';

  return (
    <div className={`w-7 h-7 rounded-full border-2 ${borderClass} flex items-center justify-center relative flex-shrink-0`}>
      <span className="text-xs font-bold text-gray-200">{rank}</span>
      {success === true && (
        <Check className="absolute -top-1 -right-1 w-3 h-3 text-green-500 bg-gray-800 rounded-full" />
      )}
      {success === false && (
        <X className="absolute -top-1 -right-1 w-3 h-3 text-red-500 bg-gray-800 rounded-full" />
      )}
    </div>
  );
}

interface SortableTeamRowProps {
  teamId: number;
  index: number;
  rank: number;
  team: Team;
  bettable: boolean;
  standingsBet?: GeneralBet;
  qualBet?: GeneralBet;
  isThirdPlace: boolean;
  isQualifiedThird: boolean;
  onToggleThirdPlace: (teamId: number) => void;
}

function SortableTeamRow({
  teamId,
  rank,
  team,
  bettable,
  standingsBet,
  qualBet,
  isThirdPlace,
  isQualifiedThird,
  onToggleThirdPlace,
}: SortableTeamRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: teamId,
    disabled: !bettable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const rankSuccess = standingsBet?.success;
  const qualSuccess = !bettable ? qualBet?.success : undefined;

  let badgeText: string;
  let badgeClass: string;

  if (rank <= 2) {
    badgeText = 'עולה';
    badgeClass = qualSuccess === false
      ? 'border border-red-400 text-red-300 bg-red-900'
      : 'border border-green-400 text-green-300 bg-green-900';
  } else if (isThirdPlace) {
    if (isQualifiedThird) {
      badgeText = 'עולה';
      if (qualSuccess === false) {
        badgeClass = bettable
          ? 'border border-dashed border-red-400 text-red-300 bg-red-900'
          : 'border border-red-400 text-red-300 bg-red-900';
      } else {
        badgeClass = bettable
          ? 'border border-dashed border-green-400 text-green-300 bg-green-900'
          : 'border border-green-400 text-green-300 bg-green-900';
      }
    } else {
      badgeText = bettable ? 'לא עולה' : 'בחוץ';
      badgeClass = bettable
        ? 'border border-dashed border-red-400 text-red-300 bg-red-900'
        : 'bg-gray-700 text-gray-200';
    }
  } else {
    badgeText = 'בחוץ';
    badgeClass = 'bg-gray-700 text-gray-200';
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2 py-2 rounded border transition-colors ${
        isDragging ? 'border-stake-blue bg-blue-900 bg-opacity-20' : 'border-gray-700 bg-gray-900'
      }`}
    >
      {bettable && (
        <span
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing p-2 -m-2"
        >
          <GripVertical className="w-7 h-7 text-gray-500 flex-shrink-0" />
        </span>
      )}

      <RankCircle rank={rank} success={bettable ? undefined : rankSuccess} />

      {team.logo_url && (
        <img src={team.logo_url} alt={team.name} className="w-5 h-5 object-contain flex-shrink-0" />
      )}

      <span className="text-gray-200 text-sm flex-1 truncate">{team.name}</span>

      <Badge
        onClick={isThirdPlace && bettable ? () => onToggleThirdPlace(teamId) : undefined}
        className={`flex-shrink-0 relative ${badgeClass} ${isThirdPlace && bettable ? 'cursor-pointer active:opacity-80' : 'cursor-default'}`}
      >
        {badgeText}
        {qualSuccess === true && <Check className="absolute -top-1 -right-1 w-3 h-3 text-green-500 bg-gray-800 rounded-full" />}
        {qualSuccess === false && <X className="absolute -top-1 -right-1 w-3 h-3 text-red-500 bg-gray-800 rounded-full" />}
      </Badge>
    </div>
  );
}

const GroupRankingBet: React.FC<GroupRankingBetProps> = ({
  qualConfig,
  standingsConfig,
  groups,
  teams,
  userBets,
  bettable,
  onBetsUpdated,
}) => {
  const [groupOrders, setGroupOrders] = useState<Record<string, number[]>>({});
  const [thirdPlaceQualifiers, setThirdPlaceQualifiers] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const thirdPlaceSlots = qualConfig.view_schema
    ? qualConfig.view_schema.size - groups.length * 2
    : 0;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
  );

  useEffect(() => {
    const initialOrders: Record<string, number[]> = {};

    groups.forEach(group => {
      const groupTeams = teams.filter(t => t.group_id === group.id);
      const ranked = groupTeams
        .map(team => {
          const bet = userBets[`${standingsConfig.id}_${team.id}`];
          const rank = bet?.prediction?.rank;
          return rank != null ? { teamId: team.id, rank: rank as number } : null;
        })
        .filter((x): x is { teamId: number; rank: number } => x !== null);

      if (ranked.length === groupTeams.length) {
        ranked.sort((a, b) => a.rank - b.rank);
        initialOrders[group.id.toString()] = ranked.map(r => r.teamId);
      } else {
        initialOrders[group.id.toString()] = groupTeams.map(t => t.id);
      }
    });

    setGroupOrders(initialOrders);

    const thirdPlace = new Set<number>();
    Object.values(userBets)
      .filter(bet => bet.bet_id === qualConfig.id && bet.prediction?.id != null && bet.prediction?.group_id != null)
      .forEach(bet => {
        const teamId = bet.prediction.id as number;
        const groupId = (bet.prediction.group_id as number).toString();
        if (initialOrders[groupId]?.[2] === teamId) {
          thirdPlace.add(teamId);
        }
      });
    setThirdPlaceQualifiers(thirdPlace);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, teams, qualConfig.id, standingsConfig.id]);

  const submitBets = async (orders: Record<string, number[]>, thirdPlace: Set<number>, includeStandings = true) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const qualPredictions: object[] = [];
      Object.entries(orders).forEach(([groupId, teamIds]) => {
        if (teamIds[0] != null) qualPredictions.push({ id: teamIds[0], group_id: parseInt(groupId, 10) });
        if (teamIds[1] != null) qualPredictions.push({ id: teamIds[1], group_id: parseInt(groupId, 10) });
      });
      thirdPlace.forEach(teamId => {
        const team = teams.find(t => t.id === teamId);
        if (team?.group_id) qualPredictions.push({ id: teamId, group_id: team.group_id });
      });

      if (includeStandings) {
        const standingsPredictions = Object.entries(orders).flatMap(([groupId, teamIds]) =>
          teamIds.map((teamId, i) => ({ id: teamId, group_id: parseInt(groupId, 10), rank: i + 1 }))
        );
        const [standingsResp, qualResp] = await Promise.all([
          placeBatchGeneralBets(standingsConfig.id, standingsPredictions),
          placeBatchGeneralBets(qualConfig.id, qualPredictions),
        ]);
        onBetsUpdated(standingsResp, qualResp);
      } else {
        const qualResp = await placeBatchGeneralBets(qualConfig.id, qualPredictions);
        onBetsUpdated([], qualResp);
      }
    } catch (err) {
      console.error('Error submitting group bets:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent, groupId: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const list = groupOrders[groupId] || [];
    const oldIndex = list.indexOf(active.id as number);
    const newIndex = list.indexOf(over.id as number);
    if (oldIndex === -1 || newIndex === -1) return;

    const newList = arrayMove(list, oldIndex, newIndex);
    const newOrders = { ...groupOrders, [groupId]: newList };

    const newThirdPlace = new Set(thirdPlaceQualifiers);
    const oldThird = list[2];
    const newThird = newList[2];
    if (oldThird != null && oldThird !== newThird) {
      newThirdPlace.delete(oldThird);
    }

    setGroupOrders(newOrders);
    setThirdPlaceQualifiers(newThirdPlace);

    if (bettable) submitBets(newOrders, newThirdPlace);
  };

  const toggleThirdPlace = (teamId: number) => {
    if (!bettable || submitting) return;
    const newSet = new Set(thirdPlaceQualifiers);
    if (newSet.has(teamId)) {
      newSet.delete(teamId);
    } else {
      if (newSet.size >= thirdPlaceSlots) return;
      newSet.add(teamId);
    }
    setThirdPlaceQualifiers(newSet);
    submitBets(groupOrders, newSet, false);
  };

  return (
    <div className="space-y-4">
      {groups.map(group => {
        const gKey = group.id.toString();
        const orderedTeamIds = groupOrders[gKey] || [];

        return (
          <div key={group.id} className="bg-gray-800 border border-gray-700 rounded p-3">
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-gray-200">בית {group.name}</h3>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={e => handleDragEnd(e, gKey)}
            >
              <SortableContext items={orderedTeamIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {orderedTeamIds.map((teamId, index) => {
                    const rank = index + 1;
                    const team = teams.find(t => t.id === teamId);
                    if (!team) return null;

                    return (
                      <SortableTeamRow
                        key={teamId}
                        teamId={teamId}
                        index={index}
                        rank={rank}
                        team={team}
                        bettable={bettable}
                        standingsBet={userBets[`${standingsConfig.id}_${teamId}`]}
                        qualBet={userBets[`${qualConfig.id}_${teamId}`]}
                        isThirdPlace={rank === 3}
                        isQualifiedThird={rank === 3 && thirdPlaceQualifiers.has(teamId)}
                        onToggleThirdPlace={toggleThirdPlace}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        );
      })}
    </div>
  );
};

export default GroupRankingBet;
